/**
 * Client-side record of which support-access grant this tab is acting under.
 *
 * This is ONLY an index. It grants nothing: the server holds the grant row, and
 * a forged value buys an attacker nothing without the staff member's Firebase
 * token. Everything the interface claims about a session is read back from the
 * SERVER (`GET support-access/session`), never from here — see
 * `useSupportAccessSession`.
 *
 * 🔑 `sessionStorage`, deliberately NOT `localStorage`. A support session
 * belongs to the tab the staff member is working in. localStorage is shared
 * across every tab in the browser, so opening a second tab to look something up
 * would silently put it inside the customer's session too — and closing one tab
 * would end the other's. sessionStorage is per-tab and survives a refresh, which
 * is exactly the lifetime a session should have.
 */
const STORAGE_KEY = "equility.supportAccess.grantId";

/** A grant id is a uuid. Anything else is discarded rather than sent. */
const UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Storage can throw — Safari private mode, and any browser with site data
 * blocked. A session that cannot be remembered is a degraded experience; a
 * crash on every request is an outage, so every access is guarded.
 */
function safeStorage(): Storage | null {
	try {
		return typeof window === "undefined" ? null : window.sessionStorage;
	} catch {
		return null;
	}
}

export function getActAsGrantId(): string | null {
	try {
		const raw = safeStorage()?.getItem(STORAGE_KEY);
		if (!raw) return null;
		return UUID_RE.test(raw) ? raw : null;
	} catch {
		return null;
	}
}

export function setActAsGrantId(grantId: string): void {
	if (!UUID_RE.test(grantId)) return;
	try {
		safeStorage()?.setItem(STORAGE_KEY, grantId);
	} catch {
		// Non-fatal: the header simply will not be sent on the next page load.
	}
}

export function clearActAsGrantId(): void {
	try {
		safeStorage()?.removeItem(STORAGE_KEY);
	} catch {
		// Non-fatal.
	}
}

/** Header name. Must match SUPPORT_ACCESS_HEADER in equility-api. */
export const ACT_AS_HEADER = "X-Equility-Act-As";

/**
 * Codes the API puts on a 401 that refuses the act-as header. Must match
 * SUPPORT_ACCESS_ENDED_CODE / SUPPORT_ACCESS_REFUSED_CODE in equility-api.
 *
 * 🔑 They exist so the interceptor can tell "your support session is over" from
 * "your own login is stale". The first is fixed by dropping the header and
 * carrying on as yourself; the second, eventually, by logging out. Reading the
 * wrong one for the other is how an expired support session used to end a staff
 * member's own session.
 */
export const SUPPORT_ACCESS_ENDED_CODE = "SUPPORT_ACCESS_ENDED";
export const SUPPORT_ACCESS_REFUSED_CODE = "SUPPORT_ACCESS_REFUSED";
